import type { Route } from 'vue-router';
import {
    type Deployment,
    type DesignDeviceMetricToVarIdMap,
    activeOperatorAuthMethodSchema,
    deploymentConfigSchema,
} from '@redviking/argonaut-util/src/mes/appletDeployment.zod';
import { apiTrpcHttpClient } from '@redviking/argonaut-core-ui/src/util/api-trpc';
import { fromZodError } from 'zod-validation-error';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import { applySparkplugRequirements } from './util/sparkplug-formatter';
import { CausalError } from '@redviking/causal-error';
import { z } from 'zod';
import type { ExtendedEntityParameters } from 'src/components/EntityDetail';
import { validateAppletDeployment } from './appletDeployment.validations';
import { type ValidationResult } from 'types';
import { GetDeploymentDocument } from 'types/db';
import { gqlClient } from 'src/util/gql-client';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { DeployParamKind, type DeployParamWithId, getDeployParams } from '@redviking/argonaut-util/src/mes/deployParam.zod';
import { appletDesignSchema } from '@redviking/argonaut-util/types/mes/applet-designs/applet-design-config.zod';
import { getInitialDeployParamValue } from './appletDeployment.store';
import { GetAppletDesignVersionDocument } from '@redviking/argonaut-core-ui/types/db-ops';

export interface ReferencedDesignVersion {
    designId: string;
    designName: string;
    designVersionId: string;
    designVersionNumber: number;
    designVersionConfig: Latest.AppletDesignVersionConfig;
    deployParams: DeployParamWithId[];
}

export type DeploymentEntity = {
    deployment: Deployment;
    /**
     * it's assumed that if a design version is missing here, it will also be missing from the deployment.
     * 
     * it's also assumed that this property will only be modified by `deploymentEntityUtils` functions,
     * and it will be kept in sync with the deployment's linkedDesignVersions property.
     */
    referencedDesignVersions: {
        [designVersionId: string]: ReferencedDesignVersion;
    };
}

function createNewLdv (referencedDesignVersion: ReferencedDesignVersion, opts: { sparkplugNodeExpose: boolean } = { sparkplugNodeExpose: true }): Deployment['linkedDesignVersions'][number] {
    // determine the variables to expose over sparkplug
    const sparkplugNodeVars = Object.entries(referencedDesignVersion.designVersionConfig.appletVariables).filter(([ , varCfg ]) => varCfg.sparkplug.tracked);

    const newLdv: Deployment['linkedDesignVersions'][number] = {
        config: {
            sparkplugNodeExpose: sparkplugNodeVars.length > 0 && opts.sparkplugNodeExpose
                ? {
                    designNameAsDeviceId: applySparkplugRequirements(referencedDesignVersion.designName),
                    vars: sparkplugNodeVars.map(([ varId, varCfg ]) => {
                        const ret: DesignDeviceMetricToVarIdMap = {
                            varId,
                            metricName: applySparkplugRequirements(varCfg.name),
                        };
                        return ret;
                    }),
                }
                : null,
        },
        designVersion: {
            id: referencedDesignVersion.designVersionId,
            number: referencedDesignVersion.designVersionNumber,
            design: {
                id: referencedDesignVersion.designId,
                name: referencedDesignVersion.designName,
            },
        },
    };

    return newLdv;
}

/**
 * a collection of functions that transform a `DeploymentEntity`.
 * 
 * This would normally just live in vuex, but we need to use it outside of an entity context (the wizard)
 */
export const deploymentEntityUtils = {
    async fetchAndValidateDesignVersion (opts: {
        designVersionId: string;
    }): Promise<ReferencedDesignVersion> {
        const {
            applet_design_version_by_pk: appletDesignVersion,
        } = await gqlClient.request({
            document: GetAppletDesignVersionDocument,
            variables: {
                appletDesignVersionId: opts.designVersionId,
            },
        });
        if (!appletDesignVersion) {
            throw new Error('Failed to load applet design version');
        }

        const parsedConfig = appletDesignSchema.safeParse(appletDesignVersion.config);
        if (!parsedConfig.success) {
            throw new Error(`Applet design version config is invalid: ${fromZodError(parsedConfig.error)}`);
        }

        const deployParams = getDeployParams(parsedConfig.data, appletDesignSchema);

        return {
            designId: appletDesignVersion.design.id,
            designName: appletDesignVersion.design.name,
            designVersionId: appletDesignVersion.id,
            designVersionConfig: parsedConfig.data,
            designVersionNumber: appletDesignVersion.version_number || 1, // new design versions won't have a version number
            deployParams,
        };
    },
    addLinkedDesignVersion (opts: {
        deploymentEntity: DeploymentEntity;
        designId: string;
        designName: string;
        designVersionId: string;
        designVersionNumber: number;
        /** if you already have the design version config you can pass it here and avoid a fetch */
        designVersionConfig: Latest.AppletDesignVersionConfig;
        deployParams: DeployParamWithId[];
        sparkplugNodeExpose?: boolean;
    }): DeploymentEntity {
        const { deploymentEntity, designVersionId, designId, designName, designVersionConfig, deployParams } = opts;
        const newEntity = structuredClone(deploymentEntity);
        
        // if the design is already added, abort
        if (newEntity.deployment.linkedDesignVersions.find(ldv => ldv.designVersion.id === designVersionId)) {
            throw new Error(`Design version ${designVersionId} is already linked to this deployment`);
        }

        newEntity.referencedDesignVersions[designVersionId] = {
            designId,
            designName,
            designVersionId,
            designVersionConfig,
            deployParams,
            designVersionNumber: opts.designVersionNumber,
        };

        // initial config for the new ldv
        const newLdv: Deployment['linkedDesignVersions'][number] = createNewLdv(newEntity.referencedDesignVersions[designVersionId], { sparkplugNodeExpose: opts.sparkplugNodeExpose ?? true });

        // ensure the deploy params values are updated with the new deploy params from the design version config
        for (const deployParam of deployParams) {
            const defaultDeployParam = deploymentEntity.deployment.config.deployParamVals[deployParam.deployParamId];
            if (defaultDeployParam === undefined) {
                newEntity.deployment.config.deployParamVals[deployParam.deployParamId] = getInitialDeployParamValue(deployParam);
            } else if (defaultDeployParam.kind !== deployParam.deployParamKind) {
                throw new Error(`Applet Design "${newLdv.designVersion.design.name}" (${newLdv.designVersion.number}) has deployment parameters that conflict with existing deployment parameters. Incoming deployment parameter "${deployParam.deployParamId}" has type "${deployParam.deployParamKind}" while exsting deployment parameter has type "${defaultDeployParam.kind}"`);
            } else if (defaultDeployParam.kind === DeployParamKind.localVarValue && deployParam.deployParamKind === DeployParamKind.localVarValue && typeof defaultDeployParam.val !== typeof deployParam.defaultValue) {
                throw new Error(`Applet Design "${newLdv.designVersion.design.name}" (${newLdv.designVersion.number}) has deployment parameters that conflict with existing deployment parameters. Incoming local variable deployment parameter "${deployParam.deployParamId}" expects type "${typeof deployParam.defaultValue}" while exsting local variable deployment parameter expects type "${typeof defaultDeployParam.val}"`);
            }
        }

        newEntity.deployment.linkedDesignVersions.push(newLdv);

        return newEntity;
    },
    replaceLinkedDesignVersion (opts: {
        deploymentEntity: DeploymentEntity;
        oldVersionId: string;
        newVersionId: string;
    }): DeploymentEntity {
        const newEntity = structuredClone(opts.deploymentEntity);
        const oldLdvIndex = newEntity.deployment.linkedDesignVersions.findIndex(ldv => ldv.designVersion.id === opts.oldVersionId);
        if (oldLdvIndex === -1) {
            throw new Error(`Design version ${opts.oldVersionId} is not linked to this deployment`);
        }
        const oldReferencedDesignVersion = newEntity.referencedDesignVersions[opts.oldVersionId];
        if (!oldReferencedDesignVersion) {
            throw new Error(`Design version ${opts.oldVersionId} is not referenced in this deployment entity`);
        }
        const newReferencedDesignVersion = newEntity.referencedDesignVersions[opts.newVersionId];
        if (!newReferencedDesignVersion) {
            throw new Error(`Design version ${opts.newVersionId} is not referenced in this deployment entity`);
        }

        // initial config for the new ldv
        const newLdv: Deployment['linkedDesignVersions'][number] = createNewLdv(newReferencedDesignVersion);

        // patch in the new ldv
        newEntity.deployment.linkedDesignVersions[oldLdvIndex] = newLdv;

        // search and destroy old deploy params that are no longer used by any ldv
        const oldDeployParamIds = new Set(oldReferencedDesignVersion.deployParams.map(dp => dp.deployParamId));
        for (const oldDeployParamId of oldDeployParamIds) {
            const isStillUsed = newEntity.deployment.linkedDesignVersions.some(ldv => {
                return newEntity.referencedDesignVersions[ldv.designVersion.id].deployParams.some(dp => dp.deployParamId === oldDeployParamId);
            });
            if (!isStillUsed) {
                delete newEntity.deployment.config.deployParamVals[oldDeployParamId];
            }
        }

        // ensure the deploy params values are updated with the new deploy params from the design version config
        for (const deployParam of newReferencedDesignVersion.deployParams) {
            const defaultDeployParam = newEntity.deployment.config.deployParamVals[deployParam.deployParamId];
            if (defaultDeployParam === undefined) {
                newEntity.deployment.config.deployParamVals[deployParam.deployParamId] = getInitialDeployParamValue(deployParam);
            } else if (defaultDeployParam.kind !== deployParam.deployParamKind) {
                throw new Error(`Applet Design "${newLdv.designVersion.design.name}" (${newLdv.designVersion.number}) has deployment parameters that conflict with existing deployment parameters. Incoming deployment parameter "${deployParam.deployParamId}" has type "${deployParam.deployParamKind}" while exsting deployment parameter has type "${defaultDeployParam.kind}"`);
            } else if (defaultDeployParam.kind === DeployParamKind.localVarValue && deployParam.deployParamKind === DeployParamKind.localVarValue && typeof defaultDeployParam.val !== typeof deployParam.defaultValue) {
                throw new Error(`Applet Design "${newLdv.designVersion.design.name}" (${newLdv.designVersion.number}) has deployment parameters that conflict with existing deployment parameters. Incoming local variable deployment parameter "${deployParam.deployParamId}" expects type "${typeof deployParam.defaultValue}" while exsting local variable deployment parameter expects type "${typeof defaultDeployParam.val}"`);
            }
        }

        return newEntity;
    },
};

export async function getAppletDeploymentVersion (to: Route): Promise<{ entity: DeploymentEntity }> {
    const baseActiveOperatorAuthMethods: Deployment['config']['activeOperatorAuthMethods'] = [];

    // Build base map of auth methods then later merge with existing config
    for (const authType of activeOperatorAuthMethodSchema.shape.authType.options) {
        baseActiveOperatorAuthMethods.push({ authType, access: 'disabled' });
    }
    if (to.query.mode === 'create') {
        const query = to.query as Record<string, string | undefined>;
        const deploymentName = 'New Deployment';
        const deployment: Deployment = {
            notes: '',
            version: 1,
            contextId: '',
            schemaVersion: 1,
            name: deploymentName,
            linkedDesignVersions: [],
            id: to.params.appletDeploymentId,
            runnerId: query.appletRunnerId || '',
            config: {
                deploymentSparkplugNode: {
                    version: 'spBv1.0',
                    mqttConnectionId: '019ebd21-0489-7249-bbfd-081a691bb801',
                    groupId: `${applySparkplugRequirements(accessor.auth.currentContext?.name || 'unknown-context-name')}-${applySparkplugRequirements(query.appletRunnerId || '')}`,
                    nodeId: applySparkplugRequirements(deploymentName),
                    // defaulting to false since on create a deployment will not have any linked designs, thus no designs that are using sparkplug
                    usingSparkplug: false,
                },
                deployParamVals: {},
                defaults: {
                    designVersionId: null,
                    screenId: null,
                    designId: null,
                },
                activeOperatorAuthMethods: baseActiveOperatorAuthMethods,
                activeOperatorSettings: { enabled: false },
                enablePerformanceMetrics: false, // default to false for new deployments since they won't be fully configured yet and we want to avoid noisy health updates until they're ready. users can toggle this on in the deployment settings if they want to see resource samples during configuration.
                appletEventRateLimit: { enabled: true, qty: 50, ms: 1000 },
                scriptRateLimit: { qty: 10, ms: 1000 },
            },
        };
        return { entity: { deployment, referencedDesignVersions: {} } };
    }

    const {
        applet_deployment_by_pk,
    } = await gqlClient.request({
        document: GetDeploymentDocument,
        variables: {
            deploymentId: to.params.appletDeploymentId,
        },
    });

    if (!applet_deployment_by_pk) {
        throw new Error(`Deployment with id "${to.params.appletDeploymentId}" not found`);
    }

    try {
        let deploymentEntity: DeploymentEntity = {
            deployment: {
                schemaVersion: 1,
                id: applet_deployment_by_pk.id,
                name: applet_deployment_by_pk.name,
                notes: applet_deployment_by_pk.notes,
                version: applet_deployment_by_pk.version,
                runnerId: applet_deployment_by_pk.runner_id,
                contextId: applet_deployment_by_pk.context.id,
                config: deploymentConfigSchema.parse(applet_deployment_by_pk.config),
                linkedDesignVersions: [],
            },
            referencedDesignVersions: {},
        };

        for (const ldv of applet_deployment_by_pk.linked_design_versions) {
            try {
                const referencedDesignVersion = await deploymentEntityUtils.fetchAndValidateDesignVersion({ designVersionId: ldv.design_version.id });
                const sparkplugNodeExpose = typeof ldv.config === 'object' && ldv.config !== null && 'sparkplugNodeExpose' in ldv.config && ldv.config.sparkplugNodeExpose !== null;
                deploymentEntity = deploymentEntityUtils.addLinkedDesignVersion({
                    deploymentEntity,
                    designId: referencedDesignVersion.designId,
                    designName: referencedDesignVersion.designName,
                    designVersionId: referencedDesignVersion.designVersionId,
                    designVersionNumber: referencedDesignVersion.designVersionNumber,
                    designVersionConfig: referencedDesignVersion.designVersionConfig,
                    deployParams: referencedDesignVersion.deployParams,
                    sparkplugNodeExpose,
                });
            } catch (err) {
                console.error(`Failed to fetch and validate design version with id "${ldv.design_version.id}":`, err);
            }
        }
        
        return { entity: deploymentEntity };
    } catch (err) {
        throw new CausalError('Deployment is invalid and could not be read', fromZodError(err as z.ZodError));
    }
}

export async function saveAppletDeploymentVersion (entity: Deployment): Promise<void> {
    await apiTrpcHttpClient.applet.deployment.upsert.mutate({
        deployment: {
            currentVersion: entity.version,
            linkedDesignVersions: entity.linkedDesignVersions.map(ldv => ({
                designVersionId: ldv.designVersion.id,
                config: ldv.config,
            })),
            name: entity.name,
            notes: entity.notes,
            runnerId: entity.runnerId,
            id: entity.id,
            config: {
                deploymentSparkplugNode: {
                    ...entity.config.deploymentSparkplugNode,
                    nodeId: applySparkplugRequirements(entity.name),
                },
                deployParamVals: entity.config.deployParamVals,
                defaults: {
                    designVersionId: entity.config.defaults?.designVersionId || null,
                    screenId: entity.config.defaults?.screenId || null,
                },
                activeOperatorAuthMethods: entity.config.activeOperatorAuthMethods,
                activeOperatorSettings: entity.config.activeOperatorSettings,
                enablePerformanceMetrics: entity.config.enablePerformanceMetrics,
                appletEventRateLimit: entity.config.appletEventRateLimit,
                scriptRateLimit: entity.config.scriptRateLimit,
            },
        },
    });
}

export const saveAppletDeploymentVersionEntity = async (payload: ExtendedEntityParameters<'appletDeployment'>): Promise<ValidationResult> => {
    const {
        entity,
    } = payload;
    const validationResult = await validateAppletDeployment(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    try {
        await saveAppletDeploymentVersion(entity.deployment);
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Deployment: "${JSON.stringify(err)}"`,
        };
    }
};
